import React, { useEffect, useMemo, useState } from "react";
import {
  FaBolt,
  FaBrain,
  FaCheck,
  FaCheckCircle,
  FaChevronRight,
  FaDatabase,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaEye,
  FaFingerprint,
  FaHistory,
  FaKey,
  FaLock,
  FaRobot,
  FaSearch,
  FaShieldAlt,
  FaTasks,
  FaUserShield,
} from "react-icons/fa";
import { useDarkweb } from "../../../context/Dashboard/contextDarkweb";
import { useChat } from "../../../context/contextChat";
import { useDesign } from "../../../context/contextDesign";
import "./darkweb-intelligence-center.css";

const FILTERS = [
  { id: "all", label: "All evidence" },
  { id: "attention", label: "Needs attention" },
  { id: "critical", label: "Critical" },
  { id: "unverified", label: "Unverified" },
];

const AI_QUESTIONS = [
  "What dark web exposures require immediate action?",
  "Are any privileged accounts exposed?",
  "Which exposed credentials may still represent current risk?",
  "Which exposed identities have the highest takeover risk?",
  "Which exposures could lead to account takeover?",
  "Which exposed identities do not have verified MFA?",
  "What should the SOC investigate today?",
  "Give me a remediation plan for this exposure.",
  "How can I manually validate this exposure?",
  "What identity controls would contain this exposure?",
  "Why did Dark Web risk reduce my SAVE Score?",
  "How can we recover the lost SAVE Score?",
  "What changed since the previous dark web assessment?",
  "Generate an executive Dark Web Exposure report.",
];

const EMPTY_SUMMARY = {
  target: "—",
  target_type: "unknown",
  total_exposures: 0,
  requires_attention: 0,
  critical: 0,
  high: 0,
  unverified: 0,
  possible_active_credentials: 0,
  privileged_identities: 0,
  risk_score: 0,
  severity: "unknown",
  identity_score: null,
  save_score_impact: 0,
  assessment_status: "inconclusive",
  score_available: false,
};

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const titleCase = (value) =>
  String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const dateLabel = (value, includeTime = false) => {
  if (!value) return "Not assessed";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not assessed";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  }).format(parsed);
};

const scoreTone = (score) => {
  if (score >= 8) return "good";
  if (score >= 6) return "watch";
  return "danger";
};

const severityTone = (severity) => {
  const normalized = String(severity || "informational").toLowerCase();
  if (normalized === "critical") return "critical";
  if (normalized === "high") return "high";
  if (normalized === "medium") return "medium";
  if (normalized === "low") return "low";
  return "neutral";
};

const providerStatusLabel = (status) => {
  switch (status) {
    case "reported":
      return "Reported";
    case "failed":
      return "Failed";
    case "ready":
      return "Ready for next assessment";
    case "not_configured":
      return "API key not configured";
    case "disabled":
      return "Disabled";
    case "not_applicable":
      return "Not applicable to this target";
    default:
      return "Not connected";
  }
};

const providerStatusTone = (status) => {
  if (status === "reported") return "reported";
  if (status === "failed") return "failed";
  if (status === "ready") return "ready";
  return "inactive";
};

const Kpi = ({ icon, label, value, helper, tone = "blue" }) => (
  <article className={`dwi-kpi dwi-kpi--${tone}`}>
    <span className="dwi-kpi__icon">{icon}</span>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{helper}</small>
    </div>
  </article>
);

const EmptyState = ({ icon = <FaDatabase />, children }) => (
  <div className="dwi-empty">
    <span>{icon}</span>
    <p>{children}</p>
  </div>
);

const DarkwebIntelligenceCenter = () => {
  const { bHeightVal } = useDesign();
  const {
    ScanLoading,
    intelligence,
    error,
    query,
    setQuery,
    selectedScanId,
    setSelectedScanId,
    scanProgress,
    initData,
    handleConfigureProvider,
    handleDisconnectProvider,
    handleScan,
    handleRemediationUpdate,
  } = useDarkweb();
  const { setPrompt, setShowChat } = useChat();
  const [activeTab, setActiveTab] = useState("evidence");
  const [filter, setFilter] = useState("all");
  const [selectedExposureId, setSelectedExposureId] = useState(null);
  const [remediationBusy, setRemediationBusy] = useState(null);
  const [providerDrafts, setProviderDrafts] = useState({});
  const [providerBusy, setProviderBusy] = useState(null);
  const [providerMessages, setProviderMessages] = useState({});
  const [providerErrors, setProviderErrors] = useState({});

  useEffect(() => {
    initData();
  }, [initData]);

  const scans = intelligence?.scans || [];
  const overview = {
    ...EMPTY_SUMMARY,
    ...(intelligence?.overview || {}),
  };
  const selectedScan =
    scans.find((scan) => scan.scan_uuid === selectedScanId) ||
    scans[0] ||
    null;
  const scanSummary = {
    ...EMPTY_SUMMARY,
    ...(selectedScan?.risk_summary || {}),
  };
  const exposures = useMemo(
    () => selectedScan?.normalized_data || [],
    [selectedScan]
  );
  const remediation = selectedScan?.remediation || {
    status: "open",
    actions: [],
  };

  useEffect(() => {
    if (
      selectedExposureId &&
      !exposures.some(
        (exposure) => exposure.exposure_id === selectedExposureId
      )
    ) {
      setSelectedExposureId(null);
    }
  }, [exposures, selectedExposureId]);

  const visibleExposures = useMemo(() => {
    if (filter === "attention") {
      return exposures.filter(
        (item) =>
          number(item.risk_score) >= 45 &&
          item.validation_status !== "unverified"
      );
    }
    if (filter === "critical") {
      return exposures.filter((item) => item.severity === "critical");
    }
    if (filter === "unverified") {
      return exposures.filter(
        (item) => item.validation_status === "unverified"
      );
    }
    return exposures;
  }, [exposures, filter]);

  const selectedExposure =
    exposures.find(
      (exposure) => exposure.exposure_id === selectedExposureId
    ) || exposures[0] || null;
  const hasInconclusiveHistory =
    number(overview.inconclusive_assessments) > 0;
  const newExposures = exposures.filter((exposure) => {
    const observedAt = new Date(exposure.first_seen || exposure.last_seen || 0);
    return (
      !Number.isNaN(observedAt.getTime()) &&
      Date.now() - observedAt.getTime() <= 24 * 60 * 60 * 1000
    );
  }).length;
  const resolvedExposures = scans.reduce((total, scan) => {
    if (scan.remediation?.status !== "verified") return total;
    return total + (scan.normalized_data?.length || 0);
  }, 0);
  const scannerHealth = scanSummary.scanner_health || {};
  const selectedSourceCoverage =
    scanSummary.source_coverage || scannerHealth;
  const providerSourceRows = useMemo(() => {
    const catalog = Array.isArray(intelligence?.sources)
      ? intelligence.sources
      : [];
    let runs = Array.isArray(selectedSourceCoverage.sources)
      ? selectedSourceCoverage.sources
      : [];

    if (
      selectedScan &&
      !runs.length &&
      (number(selectedSourceCoverage.engines_reported) > 0 ||
        number(selectedSourceCoverage.source_failures) > 0)
    ) {
      runs = [
        {
          id: "tor-indexes",
          name: "Tor source indexes",
          category: "dark_web_index",
          status:
            number(selectedSourceCoverage.source_failures) > 0
              ? "failed"
              : "reported",
          result_count: number(scanSummary.total_exposures),
          attribution: "OnionSearch source adapters",
        },
      ];
    }

    const runIds = new Set(runs.map((source) => source.id));
    const hasIndividualTorRun = runs.some(
      (source) =>
        String(source.id || "").startsWith("tor-") &&
        source.id !== "tor-indexes"
    );
    const inactiveCatalogSources = catalog
      .filter(
        (source) =>
          !runIds.has(source.id) &&
          !(source.id === "tor-indexes" && hasIndividualTorRun)
      )
      .map((source) => ({ ...source, result_count: 0 }));

    return [...runs, ...inactiveCatalogSources].sort((left, right) => {
      const order = {
        reported: 0,
        failed: 1,
        ready: 2,
        not_configured: 3,
        disabled: 4,
        not_connected: 5,
        not_applicable: 6,
      };
      return (
        (order[left.status] ?? 9) - (order[right.status] ?? 9) ||
        String(left.name).localeCompare(String(right.name))
      );
    });
  }, [
    intelligence?.sources,
    scanSummary.total_exposures,
    selectedScan,
    selectedSourceCoverage,
  ]);
  const manageableConnectors = (intelligence?.sources || []).filter(
    (source) =>
      source.can_manage &&
      Array.isArray(source.credential_fields) &&
      source.credential_fields.length > 0
  );
  const reportedProviderCount = providerSourceRows.filter(
    (source) => source.status === "reported"
  ).length;
  const failedProviderCount = providerSourceRows.filter(
    (source) => source.status === "failed"
  ).length;
  const configuredProviderCount =
    reportedProviderCount + failedProviderCount;
  const inactiveProviderCount = providerSourceRows.filter((source) =>
    ["not_configured", "not_connected", "disabled"].includes(source.status)
  ).length;
  const effectiveCoverageStatus = !selectedScan
    ? null
    : reportedProviderCount === 0
      ? "inconclusive"
      : failedProviderCount > 0 || inactiveProviderCount > 0
        ? "partial"
        : "complete";
  const selectedCoverageIncomplete = Boolean(
    selectedScan && effectiveCoverageStatus !== "complete"
  );
  const hasExposureEvidence = exposures.length > 0;
  const scoreAvailable = Boolean(
    overview.has_assessment &&
      overview.identity_score !== null &&
      (!selectedCoverageIncomplete || hasExposureEvidence)
  );
  const identityScore = scoreAvailable
    ? number(overview.identity_score)
    : 0;
  const scorePercent = Math.max(0, Math.min(100, identityScore * 10));
  const progressMatchesSelection =
    !scanProgress?.scanId ||
    !selectedScan?.scan_uuid ||
    scanProgress.scanId === selectedScan.scan_uuid;
  const coverageLabel = effectiveCoverageStatus
    ? titleCase(effectiveCoverageStatus)
    : selectedScan
      ? "Unknown"
      : "Awaiting scan";
  const sourceCount = reportedProviderCount;
  const sourceFailures = failedProviderCount;
  const completedActions = remediation.actions.filter(
    (action) => action.completed
  ).length;
  const allActionsComplete =
    remediation.actions.length > 0 &&
    completedActions === remediation.actions.length;

  const openProviderSetup = (providerId) => {
    setActiveTab("sources");
    window.setTimeout(() => {
      document
        .getElementById(`dwi-provider-${providerId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const updateProviderDraft = (providerId, fieldName, value) => {
    setProviderDrafts((current) => ({
      ...current,
      [providerId]: {
        ...(current[providerId] || {}),
        [fieldName]: value,
      },
    }));
    setProviderErrors((current) => ({ ...current, [providerId]: "" }));
    setProviderMessages((current) => ({ ...current, [providerId]: "" }));
  };

  const providerFormValid = (connector) =>
    connector.credential_fields.every((field) => {
      const value = String(
        providerDrafts[connector.id]?.[field.name] || ""
      ).trim();
      if (field.required && !value) return false;
      if (field.pattern && value && !new RegExp(field.pattern).test(value)) {
        return false;
      }
      return true;
    });

  const configureProvider = async (event, connector) => {
    event.preventDefault();
    if (!providerFormValid(connector) || providerBusy) return;
    setProviderBusy(connector.id);
    setProviderErrors((current) => ({ ...current, [connector.id]: "" }));
    setProviderMessages((current) => ({ ...current, [connector.id]: "" }));
    try {
      await handleConfigureProvider(
        connector.id,
        providerDrafts[connector.id] || {}
      );
      setProviderDrafts((current) => ({ ...current, [connector.id]: {} }));
      setProviderMessages((current) => ({
        ...current,
        [connector.id]: `${connector.name} connected and verified`,
      }));
    } catch (configurationError) {
      setProviderErrors((current) => ({
        ...current,
        [connector.id]:
          configurationError.message || `Unable to connect ${connector.name}`,
      }));
    } finally {
      setProviderBusy(null);
    }
  };

  const disconnectProvider = async (connector) => {
    if (providerBusy) return;
    setProviderBusy(connector.id);
    setProviderErrors((current) => ({ ...current, [connector.id]: "" }));
    setProviderMessages((current) => ({ ...current, [connector.id]: "" }));
    try {
      const result = await handleDisconnectProvider(connector.id);
      setProviderDrafts((current) => ({ ...current, [connector.id]: {} }));
      setProviderMessages((current) => ({
        ...current,
        [connector.id]:
          result?.configurationSource === "environment"
            ? `User credentials removed. ${connector.name} is still managed by the backend environment.`
            : `${connector.name} disconnected`,
      }));
    } catch (configurationError) {
      setProviderErrors((current) => ({
        ...current,
        [connector.id]:
          configurationError.message || `Unable to disconnect ${connector.name}`,
      }));
    } finally {
      setProviderBusy(null);
    }
  };

  const askSaveAi = (question, exposure = selectedExposure) => {
    const sanitized = exposure
      ? {
          identity: exposure.identity,
          identityType: exposure.identity_type,
          exposureType: exposure.exposure_type,
          severity: exposure.severity,
          riskScore: exposure.risk_score,
          validationStatus: exposure.validation_status,
          confidence: exposure.confidence,
          privilegedIdentity: Boolean(exposure.privileged_identity),
          executiveIdentity: Boolean(exposure.executive_identity),
          possibleActiveCredential: Boolean(
            exposure.possible_active_credential
          ),
          sourceReference: exposure.source_reference,
          sourceCount: number(exposure.source_count, 1),
          corroborated: Boolean(exposure.corroborated),
          providerConfidence: number(exposure.source_confidence),
          breachDate: exposure.breach_date || null,
          sources: Array.isArray(exposure.source_references)
            ? exposure.source_references.map((source) => ({
                provider: source.provider,
                reference: source.reference,
                confidence: source.confidence,
              }))
            : [],
          firstSeen: exposure.first_seen,
          remediationStatus: remediation.status,
        }
      : {
          monitoredTargets: overview.monitored_targets,
          totalExposures: overview.total_exposures,
          requiresAttention: overview.requires_attention,
          critical: overview.critical,
          identityRiskScore: overview.risk_score,
          saveScoreImpact: overview.save_score_impact,
        };

    setPrompt(
      [
        "Analyze this sanitized SAVE Dark Web Intelligence context.",
        "Treat validation status as authoritative. Do not infer that credentials are valid, do not request or reproduce passwords, tokens, cookies, or raw breach data, and distinguish observed evidence from hypotheses.",
        `Question: ${question}`,
        `Sanitized evidence: ${JSON.stringify(sanitized)}`,
        "Provide: evidence-based interpretation, safe validation steps, remediation owners, and management impact.",
      ].join("\n\n")
    );
    setShowChat(true);
  };

  const submitScan = (event) => {
    event.preventDefault();
    handleScan();
  };

  const updateRemediation = async (update, busyKey) => {
    if (!selectedScan) return;
    setRemediationBusy(busyKey);
    try {
      await handleRemediationUpdate(selectedScan.scan_uuid, update);
    } finally {
      setRemediationBusy(null);
    }
  };

  const topRecommendation = scanSummary.top_priority
    ? `Validate ${scanSummary.top_priority.exposure_type.toLowerCase()} and begin the assigned remediation workflow.`
    : (selectedCoverageIncomplete || hasInconclusiveHistory) && !scoreAvailable
      ? sourceFailures > 0
        ? `Review the ${sourceFailures} degraded source adapter${sourceFailures === 1 ? "" : "s"}, then retry before interpreting identity risk.`
        : inactiveProviderCount > 0
          ? `Connect the ${inactiveProviderCount} unavailable intelligence source${inactiveProviderCount === 1 ? "" : "s"}, then rerun the assessment before interpreting a clean result.`
        : "Restore source coverage, then rerun the assessment before interpreting identity risk."
      : scoreAvailable
      ? "Maintain monitoring coverage and review unverified source matches before escalation."
      : "Add an organization-owned identity or domain to establish exposure coverage.";

  return (
    <div className="dwi-shell" style={{ height: bHeightVal }}>
      <div className="dwi-page">
        <header className="dwi-header">
          <div className="dwi-header__intro">
            <span className="dwi-eyebrow">
              SAVE 3.0 · Identity Exposure Monitoring
            </span>
            <h1>Dark Web Exposure</h1>
            <p>
              Monitor authorized organizational identities for credential,
              breach and dark-web exposure.
            </p>
          </div>
          <div
            className={`dwi-hero-score dwi-hero-score--${
              scoreAvailable ? scoreTone(identityScore) : "neutral"
            }`}
            style={{ "--score": `${scorePercent}%` }}
            aria-label={
              scoreAvailable
                ? `Dark Web Exposure score ${identityScore.toFixed(1)} out of 10`
                : "Dark Web Exposure score unavailable"
            }
          >
            <div>
              <span>Exposure score</span>
              <strong>{scoreAvailable ? identityScore.toFixed(1) : "—"}</strong>
              <small>{scoreAvailable ? "out of 10" : "insufficient coverage"}</small>
            </div>
          </div>
          <div className="dwi-header__controls">
            <label>
              <span>Assessment</span>
              <select
                value={selectedScan?.scan_uuid || ""}
                onChange={(event) =>
                  setSelectedScanId(event.target.value)
                }
                disabled={!scans.length}
              >
                {!scans.length && <option value="">No assessments</option>}
                {scans.map((scan) => (
                  <option key={scan.scan_uuid} value={scan.scan_uuid}>
                    {scan.risk_summary?.target || scan.query} ·{" "}
                    {dateLabel(scan.scan_time)}
                    {scan.risk_summary?.score_available === false
                      ? " · Inconclusive"
                      : ""}
                  </option>
                ))}
              </select>
            </label>
            <span className="dwi-privacy-badge">
              <FaLock /> Sanitized AI context
            </span>
          </div>
        </header>

        <section className="dwi-scan-panel">
          <div className="dwi-scan-panel__copy">
            <span className="dwi-eyebrow">Authorized monitoring</span>
            <h2>Assess an organization-owned identifier</h2>
            <p>
              Email, domain, username, or IPv4 only. Broad keyword crawling is
              blocked.
            </p>
          </div>
          <form className="dwi-scan-form" onSubmit={submitScan}>
            <div className="dwi-input-wrap">
              <FaFingerprint />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="security@company.com, company.com, username, or IPv4"
                aria-label="Organization-owned monitoring target"
                autoComplete="off"
                maxLength={255}
              />
            </div>
            <button
              type="submit"
              disabled={ScanLoading || !query.trim()}
            >
              {ScanLoading ? (
                <>
                  <span className="dwi-spinner" /> Assessing
                </>
              ) : (
                <>
                  <FaSearch /> Run assessment
                </>
              )}
            </button>
          </form>
          {(ScanLoading ||
            (scanProgress?.status === "completed" &&
              progressMatchesSelection)) && (
            <div className="dwi-progress" aria-live="polite">
              <div>
                <span>{scanProgress?.message || "Assessment in progress"}</span>
                <strong>{number(scanProgress?.progress)}%</strong>
              </div>
              <div className="dwi-progress__track">
                <span
                  style={{
                    width: `${Math.max(
                      2,
                      Math.min(100, number(scanProgress?.progress, 3))
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </section>

        {error && (
          <div className="dwi-alert dwi-alert--error">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}
        {!error &&
          (selectedCoverageIncomplete || hasInconclusiveHistory) &&
          !scoreAvailable && (
          <div className="dwi-alert dwi-alert--warning">
            <FaExclamationTriangle />
            <span>
              {selectedScan && sourceFailures > 0
                ? `This assessment is inconclusive because ${sourceFailures} of ${configuredProviderCount} requested sources failed. No clean score or “no exposure” conclusion was produced.`
                : selectedScan && inactiveProviderCount > 0
                  ? `This assessment used ${reportedProviderCount} reporting source${reportedProviderCount === 1 ? "" : "s"}, but ${inactiveProviderCount} connector${inactiveProviderCount === 1 ? " is" : "s are"} unavailable. It is partial and is not counted as a clean 10/10 result.`
                : "Previous attempts did not establish reliable source coverage. They are inconclusive and are not counted as a 10/10 score."}
            </span>
          </div>
        )}

        {selectedScan && (
          <section
            className="dwi-provider-strip"
            aria-label="Dark Web Exposure source coverage"
          >
            <div className="dwi-provider-strip__summary">
              <span className="dwi-eyebrow">Assessment source coverage</span>
              <strong>{coverageLabel}</strong>
              <small>
                {reportedProviderCount} reported · {failedProviderCount} failed ·{" "}
                {inactiveProviderCount} not connected
              </small>
            </div>
            <div className="dwi-provider-strip__sources">
              {providerSourceRows.map((source) => (
                <article key={source.id}>
                  <i
                    className={`dwi-source-dot dwi-source-dot--${providerStatusTone(
                      source.status
                    )}`}
                  />
                  <div>
                    <strong>{source.name}</strong>
                    <small>
                      {providerStatusLabel(source.status)}
                      {source.status === "reported"
                        ? ` · ${number(source.result_count)} result(s)`
                        : ""}
                    </small>
                  </div>
                  {source.can_manage && !source.configured && (
                      <button
                        type="button"
                        className="dwi-source-configure"
                        onClick={() => openProviderSetup(source.id)}
                      >
                        Configure
                      </button>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        <section
          className="dwi-kpi-grid"
          aria-label="Dark Web Exposure key performance indicators"
        >
          <Kpi
            icon={<FaFingerprint />}
            label="Monitored identities"
            value={overview.monitored_targets || 0}
            helper={`${overview.total_assessments || 0} assessment(s)`}
            tone="blue"
          />
          <Kpi
            icon={<FaEye />}
            label="Exposed identities"
            value={overview.total_exposures || 0}
            helper={`${overview.requires_attention || 0} require attention`}
            tone="blue"
          />
          <Kpi
            icon={<FaBolt />}
            label="Critical exposures"
            value={overview.critical || 0}
            helper={`${overview.privileged_identities || 0} privileged indicators`}
            tone="red"
          />
          <Kpi
            icon={<FaKey />}
            label="New exposures"
            value={newExposures}
            helper="First observed in the last 24 hours"
            tone="orange"
          />
          <Kpi
            icon={<FaCheckCircle />}
            label="Resolved exposures"
            value={resolvedExposures}
            helper="Verified remediation records"
            tone="good"
          />
          <Kpi
            icon={<FaDatabase />}
            label="Source coverage"
            value={coverageLabel}
            helper={`${sourceCount} source(s) reported · ${sourceFailures} failed`}
            tone={sourceFailures ? "orange" : sourceCount ? "cyan" : "blue"}
          />
        </section>

        <section className="dwi-command-grid">
          <article className="dwi-card dwi-score-card">
            <div className="dwi-section-heading">
              <div>
                <span className="dwi-eyebrow">Live identity posture</span>
                <h2>Dark Web Exposure Score</h2>
              </div>
              <span
                className={`dwi-status dwi-status--${
                  scoreAvailable ? scoreTone(identityScore) : "neutral"
                }`}
              >
                <i />
                {scoreAvailable
                  ? titleCase(overview.severity)
                  : hasInconclusiveHistory
                    ? "Inconclusive"
                    : "Awaiting evidence"}
              </span>
            </div>
            <div
              className={`dwi-score-orb dwi-score-orb--${
                scoreAvailable ? scoreTone(identityScore) : "neutral"
              }`}
              style={{ "--score": `${scorePercent}%` }}
            >
              <div className="dwi-score-orb__inner">
                <span>Identity posture</span>
                <strong>
                  {scoreAvailable
                    ? identityScore.toFixed(1)
                    : "—"}
                </strong>
                <small>out of 10</small>
              </div>
            </div>
            <div className="dwi-score-details">
              <div>
                <span>SAVE score impact</span>
                <strong>
                  {scoreAvailable
                    ? number(overview.save_score_impact).toFixed(1)
                    : "—"}
                </strong>
              </div>
              <div>
                <span>Open investigations</span>
                <strong>{overview.open_investigations || 0}</strong>
              </div>
              <div>
                <span>Unverified evidence</span>
                <strong>{overview.unverified || 0}</strong>
              </div>
            </div>
            <p className="dwi-data-note">
              The score is produced only when source coverage is sufficient.
              Missing evidence is shown as inconclusive, never as a healthy
              result.
            </p>
          </article>

          <article className="dwi-card dwi-ai-card">
            <div className="dwi-section-heading">
              <div>
                <span className="dwi-eyebrow">SAVE intelligence</span>
                <h2>SAVE AI — Exposure Summary</h2>
              </div>
              <FaRobot className="dwi-heading-icon" />
            </div>
            <div className="dwi-ai-status">
              <span>Identity exposure status</span>
              <strong>
                {overview.requires_attention
                  ? `${overview.requires_attention} exposure(s) require review`
                  : hasInconclusiveHistory && !scoreAvailable
                    ? "Assessment coverage is inconclusive"
                    : "No validated priority exposure"}
              </strong>
            </div>
            <div className="dwi-recommendation">
              <FaBolt />
              <div>
                <span>Today&apos;s recommendation</span>
                <strong>{topRecommendation}</strong>
              </div>
            </div>
            <div className="dwi-question-grid">
              {AI_QUESTIONS.slice(0, 6).map((question) => (
                <button
                  type="button"
                  key={question}
                  onClick={() => askSaveAi(question)}
                >
                  <FaBrain />
                  <span>{question}</span>
                  <FaChevronRight />
                </button>
              ))}
            </div>
            <select
              className="dwi-question-select"
              defaultValue=""
              onChange={(event) => {
                if (event.target.value) {
                  askSaveAi(event.target.value);
                  event.target.value = "";
                }
              }}
            >
              <option value="" disabled>
                Ask another predefined investigation question…
              </option>
              {AI_QUESTIONS.slice(6).map((question) => (
                <option key={question} value={question}>
                  {question}
                </option>
              ))}
            </select>
            <p className="dwi-data-note">
              SAVE AI receives masked identity and risk metadata only.
            </p>
          </article>
        </section>

        <nav className="dwi-tabs" aria-label="Dark Web Exposure views">
          {[
            ["evidence", "Exposure findings", FaEye],
            ["sources", "Source coverage", FaDatabase],
            ["remediation", "Remediation", FaTasks],
            ["history", "Assessment history", FaHistory],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              className={activeTab === id ? "active" : ""}
              onClick={() => setActiveTab(id)}
            >
              <Icon />
              {label}
            </button>
          ))}
        </nav>

        {activeTab === "evidence" && (
          <section className="dwi-card dwi-evidence-card">
            <div className="dwi-section-heading dwi-section-heading--split">
              <div>
                <span className="dwi-eyebrow">Deterministic evidence</span>
                <h2>Prioritized exposure findings</h2>
                <p>
                  Review traceable identity-exposure evidence and open any
                  finding for safe validation and remediation guidance.
                </p>
              </div>
              <div className="dwi-filter-row">
                {FILTERS.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={filter === item.id ? "active" : ""}
                    onClick={() => setFilter(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {!selectedScan ? (
              <EmptyState>
                Run the first authorized assessment to create an exposure
                register.
              </EmptyState>
            ) : !visibleExposures.length ? (
              <EmptyState icon={<FaCheckCircle />}>
                No evidence matches this filter for the selected assessment.
              </EmptyState>
            ) : (
              <div className="dwi-evidence-layout">
                <div className="dwi-table-wrap">
                  <table className="dwi-table">
                    <thead>
                      <tr>
                        <th>Identity</th>
                        <th>Department</th>
                        <th>Risk</th>
                        <th>Last observed</th>
                        <th>Exposure type</th>
                        <th>Credential material</th>
                        <th>MFA</th>
                        <th>Privilege</th>
                        <th>Sources</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleExposures.map((exposure) => (
                        <tr
                          key={exposure.exposure_id}
                          className={
                            selectedExposure?.exposure_id ===
                            exposure.exposure_id
                              ? "selected"
                              : ""
                          }
                          onClick={() =>
                            setSelectedExposureId(exposure.exposure_id)
                          }
                        >
                          <td>
                            <strong>{exposure.identity}</strong>
                            <small>{titleCase(exposure.identity_type)}</small>
                          </td>
                          <td>{exposure.department || "—"}</td>
                          <td>
                            <span
                              className={`dwi-severity dwi-severity--${severityTone(
                                exposure.severity
                              )}`}
                            >
                              {titleCase(exposure.severity)}
                            </span>
                            <small>{exposure.risk_score}/100</small>
                          </td>
                          <td>{dateLabel(exposure.last_seen || exposure.first_seen)}</td>
                          <td><strong>{exposure.exposure_type}</strong></td>
                          <td>
                            {exposure.possible_active_credential
                              ? "Possible active"
                              : exposure.credential_present
                                ? titleCase(exposure.credential_present)
                                : "Not indicated"}
                          </td>
                          <td>{exposure.mfa_status ? titleCase(exposure.mfa_status) : "Unknown"}</td>
                          <td>{exposure.privileged_identity ? "Privileged" : "Standard"}</td>
                          <td>{exposure.source_count || 1}</td>
                          <td>
                            <span
                              className={`dwi-validation dwi-validation--${exposure.validation_status}`}
                            >
                              {titleCase(exposure.validation_status)}
                            </span>
                            <small>{titleCase(exposure.confidence)} confidence</small>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="dwi-table-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedExposureId(exposure.exposure_id);
                              }}
                            >
                              View <FaChevronRight />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {selectedExposure && (
                  <aside className="dwi-evidence-detail">
                    <div className="dwi-detail-title">
                      <span
                        className={`dwi-severity dwi-severity--${severityTone(
                          selectedExposure.severity
                        )}`}
                      >
                        {titleCase(selectedExposure.severity)}
                      </span>
                      <strong>{selectedExposure.exposure_type}</strong>
                      <small>{selectedExposure.exposure_id}</small>
                    </div>
                    <dl>
                      <div>
                        <dt>Masked identity</dt>
                        <dd>{selectedExposure.identity}</dd>
                      </div>
                      <div>
                        <dt>Validation</dt>
                        <dd>{titleCase(selectedExposure.validation_status)}</dd>
                      </div>
                      <div>
                        <dt>Department</dt>
                        <dd>{selectedExposure.department || "—"}</dd>
                      </div>
                      <div>
                        <dt>MFA status</dt>
                        <dd>{selectedExposure.mfa_status ? titleCase(selectedExposure.mfa_status) : "Unknown"}</dd>
                      </div>
                      <div>
                        <dt>Privilege</dt>
                        <dd>{selectedExposure.privileged_identity ? "Privileged" : "Standard"}</dd>
                      </div>
                      <div>
                        <dt>Source count</dt>
                        <dd>{selectedExposure.source_count || 1}</dd>
                      </div>
                      <div>
                        <dt>Corroboration</dt>
                        <dd>
                          {selectedExposure.corroborated
                            ? "Independent sources agree"
                            : "Single-provider evidence"}
                        </dd>
                      </div>
                      <div>
                        <dt>Provider confidence</dt>
                        <dd>
                          {Math.round(
                            number(selectedExposure.source_confidence) * 100
                          )}
                          %
                        </dd>
                      </div>
                      <div>
                        <dt>Source reference</dt>
                        <dd>{selectedExposure.source_reference}</dd>
                      </div>
                      <div>
                        <dt>Breach date</dt>
                        <dd>{selectedExposure.breach_date || "Not reported"}</dd>
                      </div>
                      <div>
                        <dt>First observed</dt>
                        <dd>{dateLabel(selectedExposure.first_seen, true)}</dd>
                      </div>
                      <div>
                        <dt>Credential status</dt>
                        <dd>
                          {titleCase(selectedExposure.credential_present)}
                        </dd>
                      </div>
                      <div>
                        <dt>Password handling</dt>
                        <dd>Never collected or displayed</dd>
                      </div>
                    </dl>
                    {Array.isArray(selectedExposure.source_references) &&
                      selectedExposure.source_references.length > 0 && (
                        <div className="dwi-source-attribution">
                          <span>Evidence sources</span>
                          <div>
                            {selectedExposure.source_references.map((source) => (
                              <article
                                key={`${source.provider_id}-${source.reference}`}
                              >
                                <div>
                                  <strong>{source.provider}</strong>
                                  <small>{source.reference}</small>
                                </div>
                                <span>
                                  {Math.round(number(source.confidence) * 100)}%
                                  confidence
                                </span>
                                {source.attribution_url && (
                                  <a
                                    href={source.attribution_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Attribution <FaExternalLinkAlt />
                                  </a>
                                )}
                              </article>
                            ))}
                          </div>
                        </div>
                      )}
                    <div className="dwi-detail-note">
                      <FaShieldAlt />
                      <span>
                        This is indexed source metadata, not proof that a
                        credential is valid.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="dwi-primary-action"
                      onClick={() =>
                        askSaveAi(
                          "Give me a safe validation and remediation plan for this exposure.",
                          selectedExposure
                        )
                      }
                    >
                      <FaRobot /> Ask SAVE AI
                    </button>
                  </aside>
                )}
              </div>
            )}
          </section>
        )}

        {activeTab === "sources" && (
          <section id="dwi-provider-setup" className="dwi-card dwi-sources-card">
            <div className="dwi-section-heading dwi-section-heading--split">
              <div>
                <span className="dwi-eyebrow">Intelligence fabric</span>
                <h2>Connector coverage and source health</h2>
                <p>
                  Every available, failed, and planned provider is shown
                  explicitly. Only sources that reported are counted as
                  assessment evidence.
                </p>
              </div>
              <span className="dwi-confidence">
                {reportedProviderCount} reporting provider(s)
              </span>
            </div>
            <div className="dwi-connector-grid">
              {manageableConnectors.map((connector) => {
                const busy = providerBusy === connector.id;
                const draft = providerDrafts[connector.id] || {};
                const configurationDescription =
                  connector.configuration_source === "environment"
                    ? "Connected through the backend environment"
                    : connector.configured
                      ? "Connected with verified, encrypted credentials"
                      : `Connect your ${connector.name} subscription`;

                return (
                  <div
                    id={`dwi-provider-${connector.id}`}
                    className="dwi-hibp-setup"
                    key={connector.id}
                  >
                    <div className="dwi-hibp-setup__heading">
                      <span className="dwi-hibp-setup__icon">
                        <FaKey />
                      </span>
                      <div>
                        <strong>{connector.name}</strong>
                        <small>{configurationDescription}</small>
                      </div>
                      <span
                        className={`dwi-hibp-status ${
                          connector.configured
                            ? "dwi-hibp-status--connected"
                            : ""
                        }`}
                      >
                        {connector.configured ? <FaCheckCircle /> : <FaLock />}
                        {connector.configured ? "Connected" : "Not configured"}
                      </span>
                    </div>
                    <form
                      className="dwi-hibp-setup__form"
                      onSubmit={(event) => configureProvider(event, connector)}
                    >
                      <div className="dwi-provider-fields">
                        {connector.credential_fields.map((field) => {
                          const inputId = `${connector.id}-${field.name}`;
                          return (
                            <label htmlFor={inputId} key={field.name}>
                              <span>{field.label}</span>
                              <input
                                id={inputId}
                                type={field.type || "text"}
                                value={draft[field.name] || ""}
                                onChange={(event) =>
                                  updateProviderDraft(
                                    connector.id,
                                    field.name,
                                    event.target.value
                                  )
                                }
                                placeholder={field.placeholder || field.label}
                                autoComplete={
                                  field.type === "password"
                                    ? "new-password"
                                    : "off"
                                }
                                spellCheck="false"
                                pattern={field.pattern || undefined}
                                required={Boolean(field.required)}
                                maxLength={field.name === "apiUrl" ? 1000 : 500}
                              />
                            </label>
                          );
                        })}
                      </div>
                      <div className="dwi-provider-actions">
                        <button
                          type="submit"
                          className="dwi-primary-action"
                          disabled={!providerFormValid(connector) || Boolean(providerBusy)}
                        >
                          {busy ? "Validating…" : "Validate & connect"}
                        </button>
                        {connector.can_disconnect && (
                          <button
                            type="button"
                            className="dwi-secondary-action"
                            onClick={() => disconnectProvider(connector)}
                            disabled={Boolean(providerBusy)}
                          >
                            Disconnect
                          </button>
                        )}
                      </div>
                      <small>
                        The backend validates this subscription before encrypting
                        its credentials. Saved secrets are never returned to the
                        browser. {" "}
                        {connector.attribution_url && (
                          <a
                            href={connector.attribution_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Provider access and documentation <FaExternalLinkAlt />
                          </a>
                        )}
                      </small>
                      {providerErrors[connector.id] && (
                        <span className="dwi-hibp-feedback dwi-hibp-feedback--error">
                          <FaExclamationTriangle /> {providerErrors[connector.id]}
                        </span>
                      )}
                      {providerMessages[connector.id] && (
                        <span className="dwi-hibp-feedback dwi-hibp-feedback--success">
                          <FaCheckCircle /> {providerMessages[connector.id]}
                        </span>
                      )}
                    </form>
                  </div>
                );
              })}
            </div>
            <div className="dwi-source-register">
              {providerSourceRows.map((source) => (
                <article key={source.id}>
                  <i
                    className={`dwi-source-dot dwi-source-dot--${providerStatusTone(
                      source.status
                    )}`}
                  />
                  <div>
                    <strong>{source.name}</strong>
                    <small>
                      {titleCase(source.category || "intelligence source")}
                    </small>
                  </div>
                  <div>
                    <strong>{providerStatusLabel(source.status)}</strong>
                    <small>
                      {source.status === "reported"
                        ? `${number(source.result_count)} normalized result(s)`
                        : source.error_message || "Not counted as coverage"}
                    </small>
                  </div>
                  <div>
                    {source.attribution_url ? (
                      <a
                        href={source.attribution_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {source.attribution || "Provider attribution"}{" "}
                        <FaExternalLinkAlt />
                      </a>
                    ) : (
                      <span>{source.attribution || "Connector pending"}</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
            <p className="dwi-data-note">
              Connectors are queried only for supported assessment target types.
              Provider keys and raw credential material are never returned to
              the browser or sent to SAVE AI.
            </p>
          </section>
        )}

        {activeTab === "remediation" && (
          <section className="dwi-card dwi-remediation-card">
            <div className="dwi-section-heading dwi-section-heading--split">
              <div>
                <span className="dwi-eyebrow">Response workflow</span>
                <h2>Remediate, verify, and recover score</h2>
                <p>
                  Verification remains manual until SAVE receives
                  authoritative identity and endpoint integrations.
                </p>
              </div>
              <span className={`dwi-remediation-status dwi-remediation-status--${remediation.status}`}>
                {titleCase(remediation.status)}
              </span>
            </div>

            {!selectedScan ? (
              <EmptyState>No assessment is selected.</EmptyState>
            ) : remediation.actions.length === 0 ? (
              <EmptyState icon={<FaCheckCircle />}>
                No remediation workflow is required because this assessment
                contains no observed exposure evidence.
              </EmptyState>
            ) : (
              <>
                <div className="dwi-remediation-progress">
                  <div>
                    <span>Response completion</span>
                    <strong>
                      {completedActions}/{remediation.actions.length} actions
                    </strong>
                  </div>
                  <div>
                    <span
                      style={{
                        width: `${
                          remediation.actions.length
                            ? (completedActions /
                                remediation.actions.length) *
                              100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                <div className="dwi-action-list">
                  {remediation.actions.map((action) => (
                    <button
                      type="button"
                      key={action.id}
                      className={action.completed ? "completed" : ""}
                      disabled={remediationBusy === action.id}
                      onClick={() =>
                        updateRemediation(
                          {
                            actionId: action.id,
                            completed: !action.completed,
                            status:
                              remediation.status === "open"
                                ? "investigating"
                                : undefined,
                          },
                          action.id
                        )
                      }
                    >
                      <span className="dwi-check">
                        {action.completed && <FaCheck />}
                      </span>
                      <div>
                        <strong>{action.label}</strong>
                        <small>
                          Owner: {action.owner}
                          {action.completed_at
                            ? ` · completed ${dateLabel(action.completed_at)}`
                            : ""}
                        </small>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="dwi-remediation-actions">
                  <button
                    type="button"
                    className="dwi-secondary-action"
                    disabled={
                      remediation.status === "investigating" ||
                      remediationBusy === "investigating"
                    }
                    onClick={() =>
                      updateRemediation(
                        { status: "investigating" },
                        "investigating"
                      )
                    }
                  >
                    <FaUserShield /> Start investigation
                  </button>
                  <button
                    type="button"
                    className="dwi-secondary-action"
                    disabled={
                      !allActionsComplete ||
                      remediation.status === "actioned" ||
                      remediationBusy === "actioned"
                    }
                    onClick={() =>
                      updateRemediation({ status: "actioned" }, "actioned")
                    }
                  >
                    <FaTasks /> Mark actions complete
                  </button>
                  <button
                    type="button"
                    className="dwi-primary-action"
                    disabled={
                      !allActionsComplete ||
                      remediation.status === "verified" ||
                      remediationBusy === "verified"
                    }
                    onClick={() =>
                      updateRemediation(
                        {
                          status: "verified",
                          verificationNote:
                            "Manually verified by an authorized SAVE user.",
                        },
                        "verified"
                      )
                    }
                  >
                    <FaCheckCircle /> Verify and recover score
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {activeTab === "history" && (
          <section className="dwi-card dwi-history-card">
            <div className="dwi-section-heading">
              <div>
                <span className="dwi-eyebrow">Assessment history</span>
                <h2>Score changes and investigation state</h2>
              </div>
            </div>
            {!scans.length ? (
              <EmptyState>No assessment history is available.</EmptyState>
            ) : (
              <div className="dwi-history-list">
                {scans.map((scan) => {
                  const summary = scan.risk_summary || EMPTY_SUMMARY;
                  const scanScoreAvailable =
                    summary.score_available !== false &&
                    summary.assessment_status !== "inconclusive" &&
                    summary.identity_score !== null;
                  return (
                    <button
                      type="button"
                      key={scan.scan_uuid}
                      className={
                        selectedScan?.scan_uuid === scan.scan_uuid
                          ? "selected"
                          : ""
                      }
                      onClick={() => {
                        setSelectedScanId(scan.scan_uuid);
                        setActiveTab("evidence");
                      }}
                    >
                      <span
                        className={`dwi-history-icon dwi-history-icon--${severityTone(
                          summary.severity
                        )}`}
                      >
                        <FaFingerprint />
                      </span>
                      <div>
                        <strong>{summary.target || scan.query}</strong>
                        <small>
                          {titleCase(summary.target_type)} ·{" "}
                          {dateLabel(scan.scan_time, true)}
                        </small>
                      </div>
                      <div>
                        <strong>{summary.total_exposures || 0}</strong>
                        <small>exposures</small>
                      </div>
                      <div>
                        <strong>
                          {scanScoreAvailable
                            ? `${summary.risk_score || 0}/100`
                            : "—"}
                        </strong>
                        <small>
                          {scanScoreAvailable
                            ? `${titleCase(summary.severity)} risk`
                            : "Inconclusive"}
                        </small>
                      </div>
                      <div>
                        <strong>
                          {scanScoreAvailable
                            ? number(summary.save_score_impact).toFixed(1)
                            : "—"}
                        </strong>
                        <small>SAVE impact</small>
                      </div>
                      <span className={`dwi-remediation-status dwi-remediation-status--${scan.remediation?.status || "open"}`}>
                        {titleCase(scan.remediation?.status || "open")}
                      </span>
                      <FaChevronRight />
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <footer className="dwi-footer-note">
          <FaLock />
          <span>
            SAVE never displays leaked passwords and never sends passwords,
            session cookies, tokens, full source locations, or raw breach dumps
            to the AI model.
          </span>
        </footer>
      </div>
    </div>
  );
};

export default DarkwebIntelligenceCenter;
